import { motion, AnimatePresence } from 'framer-motion';
import { Table } from 'ka-table';
import { DataType, SortingMode } from 'ka-table/enums';
import css from './ComplaintDetail.module.css';
import styles from '../Complaints/Complaints.module.css';

import { formatDate } from '../complaintsHandler';

// ── Animation Variants (same as ComplaintsTable) ──────────────────────────
const tableVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.18 } },
  exit: { opacity: 0, transition: { duration: 0.18 } },
};

const emptyVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.25 } },
};

// ── Column & row definitions ───────────────────────────────────────────────

const TASK_COLUMNS = [
  { key: 'subject', title: 'Subject', dataType: DataType.String, width: 160 },
  { key: 'type', title: 'Type', dataType: DataType.String, width: 120 },
  { key: 'assignedTo', title: 'Assigned To', dataType: DataType.String, width: 150 },
  { key: 'dueDate', title: 'Due Date', dataType: DataType.String, width: 120 },
  { key: 'relatedPerson', title: 'Related Person', dataType: DataType.String, width: 150 },
];

const EVENT_COLUMNS = [
  { key: 'subject', title: 'Subject', dataType: DataType.String, width: 160 },
  { key: 'type', title: 'Type', dataType: DataType.String, width: 120 },
  { key: 'assignedTo', title: 'Assigned To', dataType: DataType.String, width: 150 },
  { key: 'dueDate', title: 'Due Date', dataType: DataType.String, width: 120 },
  { key: 'startTime', title: 'Start Time', dataType: DataType.String, width: 150 },
  { key: 'endTime', title: 'End Time', dataType: DataType.String, width: 150 },
  { key: 'relatedPerson', title: 'Related Person', dataType: DataType.String, width: 150 },
];

function flattenTask(raw, idx) {
  return {
    id: idx,
    subject: raw.Subject?.value ?? '—',
    type: raw.Type?.value ?? '—',
    assignedTo: raw['Owner.Name']?.value ?? '—',
    dueDate: formatDate(raw.ActivityDate?.value) ?? '—',
    relatedPerson: raw['Who.Name']?.value ?? '—',
  };
}

function flattenEvent(raw, idx) {
  return {
    id: idx,
    subject: raw.Subject?.value ?? '—',
    type: raw.Type?.value ?? '—',
    assignedTo: raw['Owner.Name']?.value ?? '—',
    dueDate: formatDate(raw.ActivityDate?.value) ?? '—',
    startTime: raw.StartDateTime?.value ? formatDate(raw.StartDateTime.value) : '—',
    endTime: raw.EndDateTime?.value ? formatDate(raw.EndDateTime.value) : '—',
    relatedPerson: raw['Who.Name']?.value ?? '—',
  };
}

// ── Shared child components (same class names as global table) ────────────
const sharedChildComponents = {
  headCell: {
    elementAttributes: () => ({ className: styles.headCell }),
  },
  cell: {
    elementAttributes: () => ({ className: styles.cell }),
  },
  dataRow: {
    elementAttributes: () => ({ className: styles.tableRow }),
  },
};

// ── Inner ka-table wrapper ────────────────────────────────────────────────
function KaDetailTable({ columns, data, emptyLabel }) {
  return (
    <motion.div className={styles.tableWrap}>
      <AnimatePresence mode="wait">
        <motion.div
          key={data.length}
          variants={tableVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <Table
            columns={columns}
            data={data}
            rowKeyField="id"
            sortingMode={SortingMode.Single}
            childComponents={sharedChildComponents}
          />
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        {data.length === 0 && (
          <motion.div
            className={styles.emptyState}
            variants={emptyVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width={40} height={40}>
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <p>No {emptyLabel} recorded.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ── Exported card wrappers ────────────────────────────────────────────────
function TableCard({ title, children }) {
  return (
    <div className={`${css.detailCard} ${css.detailFull}`}>
      <h3>{title}</h3>
      {children}
    </div>
  );
}

export const ComplaintActivityTables  = ({ tasks = [], events = [] }) => (
  <>
    {tasks.length > 0 ? <TableCard title="Tasks">
      <KaDetailTable
        columns={TASK_COLUMNS}
        data={tasks.map(flattenTask)}
        emptyLabel="tasks"
      />
    </TableCard> : ""}
    
    {events.length > 0 ? <TableCard title="Events">
      <KaDetailTable
        columns={EVENT_COLUMNS}
        data={events.map(flattenEvent)}
        emptyLabel="events"
      />
    </TableCard>
      : ""}

  </>
);